/*
 * SpellCheckManager — Orchestrierung der Rechtschreibpruefung.
 *
 * Koordiniert:
 *   - Worker-Lebenszyklus (lazy: Worker startet erst beim ersten setLanguage())
 *   - DictionaryLoader → postMessage('load_dictionary')
 *   - Per-Paragraph-Warning-Cache + Generation-Counter
 *   - Debounced markDirty() pro Paragraph (400 ms ab letzter Mutation)
 *   - Listener-API fuer den Renderer/View, der bei Updates re-rendern soll
 *
 * Phase 3: Felder-Erweiterung. Jeder Mutation-Job ist nicht laenger nur an
 * `paraId` gebunden, sondern an `(paraId, field)` mit field ∈ {'text', 'note',
 * 'info'}. Per-Field-Cache, Per-Field-Generation, Per-Field-Debounce; der
 * Worker bekommt das Feld als Property mit und gibt es im Antwort-Frame
 * unveraendert zurueck. Konsumenten (EditorView, mouseHandlers, keyboardHandlers)
 * fragen Warnings mit `(paraId, field)` ab. Der `field`-Parameter ist
 * ueberall optional mit Default `'text'`, damit bestehende Aufrufer (Phase
 * 1/2-Tests, einfache Callsites) weiter durchgehen.
 *
 * Was er NICHT tut (Phase 4):
 *   - Viewport-Priorisierung (FIFO reicht in Phase 1/2/3)
 *   - Sprachwechsel-LRU (kommt mit Multi-Lang Memory-Druck)
 *   - Diff-Review-Suppression (passiert beim Composite-Step im Renderer,
 *     nicht im Manager — Manager-Cache bleibt warm)
 *
 * Worker-Erzeugung ist injizierbar, damit Tests einen Mock-Worker einsetzen
 * koennen. Default: dedizierter Module-Worker auf SpellCheckWorker.js.
 */

const DEFAULT_DEBOUNCE_MS = 400

const FIELD_DEFAULT = 'text'

function _keyOf(paraId, field) {
    return `${paraId}|${field || FIELD_DEFAULT}`
}

export class SpellCheckManager {
    /**
     * @param {object} opts
     * @param {object}   opts.dictionaryLoader  - liefert load(lang) → {aff, dic}
     * @param {Function} [opts.workerFactory]   - () => Worker — fuer Tests injizierbar
     * @param {Function} [opts.getParagraph]    - (paraId, field) => { text, formatSpans, codeBlock }
     * @param {number}   [opts.debounceMs=400]
     */
    constructor({ dictionaryLoader, workerFactory, getParagraph, debounceMs = DEFAULT_DEBOUNCE_MS }) {
        if (!dictionaryLoader) throw new Error('SpellCheckManager: dictionaryLoader required')
        if (typeof getParagraph !== 'function') throw new Error('SpellCheckManager: getParagraph required')

        this._loader        = dictionaryLoader
        this._workerFactory = workerFactory || (() => new Worker(
            new URL('./SpellCheckWorker.js', import.meta.url),
            { type: 'module' }
        ))
        this._getParagraph  = getParagraph
        this._debounceMs    = debounceMs

        this._worker        = null
        this._primaryLang   = null
        this._fallbackLang  = null
        this._loadedLangs   = new Set()
        // Per-(paraId, field)-Maps. Schluessel via _keyOf() berechnen.
        this._generations   = new Map()
        this._warnings      = new Map()
        this._timers        = new Map()
        // Per-Paragraph-Ignore-Liste (Pro-Projekt). Wird vor dem Worker-Job
        // mit dem Personal-Dict gemerged — der Worker sieht eine Vereinigung.
        this._projectIgnoreSet = new Set()
        // Listener: (paraId, warnings, field) => void
        this._listeners     = new Set()
        // Personal-Dict-Spiegel im Main-Thread. Der Worker hat seine eigene
        // Kopie (ueber set_personal/add_personal-Messages). Den Spiegel
        // brauchen wir, damit Cloud-Sync (PUT /api/user/spellcheck) das
        // ganze Array senden kann — Server-Semantik ist Replace, nicht Add.
        this._personalSet   = new Set()

        // setLanguage-Sequenz: Latest-Wins gegen parallele Aufrufe (z.B.
        // Project-Switch + User-Pref-Pull, oder schnelles Klicken im
        // Spellcheck-Submenu). Jeder Aufruf bekommt eine eigene Sequenz-
        // Nummer; veraltete Aufrufe verwerfen ihre Seiten-Effekte vor
        // jedem postMessage. Siehe Spellcheck-Review 2026-05-10, Finding C3a.
        this._setLangSeq    = 0
    }

    // ============================================================
    // Lifecycle
    // ============================================================

    /**
     * Aktiviert die Pruefung und stellt sicher, dass Worker und das benoetigte
     * Woerterbuch da sind. Idempotent.
     *
     * Latest-Wins gegen parallele Aufrufe: jeder Aufruf bekommt eine
     * Sequenz-Nummer. Wird waehrend des `await` ein neuer setLanguage-Call
     * abgesetzt, verwirft der alte Lauf seine restlichen Seiten-Effekte
     * (postMessage, _loadedLangs.add) — sonst koennen die State-Felder
     * `_primaryLang`/`_fallbackLang`/`_loadedLangs` mit dem Worker-Inhalt
     * divergieren.
     *
     * @param {string|null} primaryLang  null = abschalten (= stop())
     * @param {string|null} [fallbackLang]
     */
    async setLanguage(primaryLang, fallbackLang = null) {
        const seq = ++this._setLangSeq
        const isStale = () => seq !== this._setLangSeq

        if (primaryLang === null) {
            this.stop()
            return
        }

        this._primaryLang  = primaryLang
        this._fallbackLang = fallbackLang

        if (!this._worker) this._worker = this._spawnWorker()

        // Primary-Sprache laden falls noch nicht da
        if (!this._loadedLangs.has(primaryLang)) {
            await this._loadAndSendDictionary(primaryLang, seq)
            if (isStale()) return
        }
        // Fallback-Sprache ebenfalls awaiten — sonst startet der Caller-Sweep
        // gegen einen Worker, der das Fallback-Dict noch nicht hat: englische
        // Worte werden initial als Tippfehler markiert und verschwinden erst,
        // wenn das `dictionary_ready`-Event den Re-Check triggert (sichtbares
        // Flackern). Fallback-Failure (z.B. 404) wird geschluckt — ohne Fallback
        // arbeitet der Manager normal weiter; das `dictionary_ready`-Event
        // bleibt als Defense-in-Depth fuer spaetere Toggles erhalten.
        if (fallbackLang && !this._loadedLangs.has(fallbackLang)) {
            try { await this._loadAndSendDictionary(fallbackLang, seq) }
            catch { /* Fallback ist optional */ }
            if (isStale()) return
        }
        // Personal-Dict mit dem (evtl. frischen) Worker resyncen — der Main-
        // Thread haelt die kanonische Liste fuer Cloud-Sync, der Worker hat
        // nach setLanguage(null)/stop() einen leeren State.
        if (this._worker) {
            const merged = this._mergedAcceptList()
            if (merged.length > 0) {
                this._worker.postMessage({ type: 'set_personal', words: merged })
            }
        }
    }

    /**
     * Gibt die aktuelle Primary-Sprache zurueck.
     * @returns {string|null} z.B. 'de' oder null wenn abgeschaltet
     */
    getLanguage() {
        return this._primaryLang
    }

    /**
     * Stoppt den Worker und purged Caches.
     *
     * Hinweis: Listener werden bewusst NICHT pro betroffener (paraId, field)
     * benachrichtigt — der Caller (`main.js#setSpellcheckLang(null)`) macht
     * unmittelbar danach `view.markDirty()`. Der Renderer liest die Warnings
     * im Render-Pfad live ueber `getWarnings(paraId, field)` (Lessons-Cluster
     * "Spellcheck Live-Lookup"), und `getWarnings` liefert nach `stop()`
     * fuer jeden Paragraph `[]`. Ein Listener-Sweep ueber alle gepurgten Keys
     * waere also doppelte Arbeit. Spellcheck-Review 2026-05-10 Finding C6.
     */
    stop() {
        if (this._worker) {
            this._worker.terminate()
            this._worker = null
        }
        this._primaryLang  = null
        this._fallbackLang = null
        this._loadedLangs.clear()
        this._generations.clear()
        this._warnings.clear()
        for (const t of this._timers.values()) clearTimeout(t)
        this._timers.clear()
    }

    // ============================================================
    // Mutation-API (vom EditorModel-Listener)
    // ============================================================

    /**
     * Markiert einen (paraId, field)-Slot als geaendert. Nach `debounceMs` ohne
     * weitere Mutation wird ein check-Job in die Queue gegeben.
     * @param {string} paraId
     * @param {'text'|'note'|'info'} [field='text']
     */
    markDirty(paraId, field = FIELD_DEFAULT) {
        if (!this._worker || !this._primaryLang) return

        const key = _keyOf(paraId, field)
        // Generation hochzaehlen — laufende Worker-Antworten mit alter
        // Generation werden in _onWorkerMessage verworfen.
        const gen = (this._generations.get(key) || 0) + 1
        this._generations.set(key, gen)

        // Debounce
        const old = this._timers.get(key)
        if (old) clearTimeout(old)
        const t = setTimeout(() => {
            this._timers.delete(key)
            this._enqueueCheck(paraId, field, gen)
        }, this._debounceMs)
        this._timers.set(key, t)
    }

    /**
     * Erzwingt einen sofortigen Check ohne Debounce (z.B. bei Viewport-
     * Initial-Sweep oder Mode-Wechsel) fuer ein einzelnes Feld.
     * @param {string} paraId
     * @param {'text'|'note'|'info'} [field='text']
     */
    checkNow(paraId, field = FIELD_DEFAULT) {
        if (!this._worker || !this._primaryLang) return
        const key = _keyOf(paraId, field)
        const old = this._timers.get(key)
        if (old) { clearTimeout(old); this._timers.delete(key) }
        const gen = (this._generations.get(key) || 0) + 1
        this._generations.set(key, gen)
        this._enqueueCheck(paraId, field, gen)
    }

    /**
     * Sweep-Helper: prueft alle drei Felder eines Paragraphs sofort durch.
     * Nutzt `checkNow` pro Feld — leere Felder werden im `_enqueueCheck`-
     * Pre-Filter sofort als "keine Warnings" verbucht, ohne Worker-Roundtrip.
     */
    checkNowAllFields(paraId) {
        this.checkNow(paraId, 'text')
        this.checkNow(paraId, 'note')
        this.checkNow(paraId, 'info')
    }

    /**
     * Loescht alle Warnings fuer ein Feld (oder alle Felder eines Paragraphs,
     * wenn field weggelassen wird).
     */
    clearWarnings(paraId, field) {
        if (field === undefined) {
            this.clearWarnings(paraId, 'text')
            this.clearWarnings(paraId, 'note')
            this.clearWarnings(paraId, 'info')
            return
        }
        const key = _keyOf(paraId, field)
        const had = this._warnings.has(key)
        this._warnings.delete(key)
        const old = this._timers.get(key)
        if (old) { clearTimeout(old); this._timers.delete(key) }
        if (had) this._notify(paraId, [], field)
    }

    // ============================================================
    // Cache-API (fuer Renderer)
    // ============================================================

    /** Aktuelle Warnings fuer einen Paragraph + Feld. */
    getWarnings(paraId, field = FIELD_DEFAULT) {
        return this._warnings.get(_keyOf(paraId, field)) || []
    }

    /** Findet Warning an einer Cursor-/Klick-Position oder null. */
    warningAt(paraId, charOffset, field = FIELD_DEFAULT) {
        const list = this._warnings.get(_keyOf(paraId, field))
        if (!list) return null
        for (const w of list) {
            if (charOffset >= w.start && charOffset <= w.start + w.length) return w
        }
        return null
    }

    /** Listener fuer "Warnings fuer (paraId, field) aktualisiert". */
    onUpdate(fn) {
        this._listeners.add(fn)
        return () => this._listeners.delete(fn)
    }

    // ============================================================
    // Personal Dict + Pro-Projekt-Ignore
    // ============================================================

    addPersonal(word) {
        if (!word) return
        this._personalSet.add(word)
        if (this._worker) {
            this._worker.postMessage({ type: 'add_personal', word })
        }
        this._dropWarningsForWord(word)
    }

    setPersonal(words) {
        const list = Array.isArray(words) ? words : Array.from(words || [])
        this._personalSet = new Set(list)
        if (this._worker) {
            this._worker.postMessage({ type: 'set_personal', words: this._mergedAcceptList() })
        }
        // Auch hier alle existierenden Warnings, die jetzt im Dict sind, entfernen.
        for (const [key, oldList] of this._warnings) {
            const filtered = oldList.filter(w => !this._personalSet.has(w.word))
            if (filtered.length !== oldList.length) {
                this._warnings.set(key, filtered)
                this._notifyByKey(key, filtered)
            }
        }
    }

    /** Liefert die aktuelle Personal-Dict-Liste sortiert (fuer Cloud-Sync). */
    getPersonal() {
        return Array.from(this._personalSet).sort()
    }

    /**
     * Setzt die Pro-Projekt-Ignore-Liste (Spec §7.4). Im Worker wird die
     * Vereinigung aus Personal-Dict + Project-Ignore gehalten — der User
     * sieht eine Welle nur, wenn das Wort in *keiner* der beiden Listen
     * steht. Beim Projekt-Wechsel wird die Liste komplett ausgetauscht.
     * @param {string[]} words
     */
    setProjectIgnore(words) {
        const list = Array.isArray(words) ? words : Array.from(words || [])
        this._projectIgnoreSet = new Set(list)
        if (this._worker) {
            this._worker.postMessage({ type: 'set_personal', words: this._mergedAcceptList() })
        }
        // Bestehende Warnings, die jetzt ignoriert werden, sofort wegnehmen
        for (const [key, oldList] of this._warnings) {
            const filtered = oldList.filter(w => !this._projectIgnoreSet.has(w.word))
            if (filtered.length !== oldList.length) {
                this._warnings.set(key, filtered)
                this._notifyByKey(key, filtered)
            }
        }
    }

    /**
     * Faegt ein Wort zur Pro-Projekt-Ignore-Liste hinzu und unterdrueckt
     * sofort alle laufenden Warnings darauf. Aufrufer (z.B. SpellCheckMenu
     * → onIgnoreInProject) ist dafuer zustaendig, das Wort serverseitig im
     * `projects.spellcheck_ignored`-Array zu persistieren.
     */
    addProjectIgnore(word) {
        if (!word || this._projectIgnoreSet.has(word)) return
        this._projectIgnoreSet.add(word)
        if (this._worker) {
            // Worker faehrt die Vereinigung als "Personal" — Engine kennt
            // keine Trennung Personal/Project. Add-only-Pfad reicht hier.
            this._worker.postMessage({ type: 'add_personal', word })
        }
        this._dropWarningsForWord(word)
    }

    /** Liefert die aktuelle Pro-Projekt-Ignore-Liste sortiert. */
    getProjectIgnore() {
        return Array.from(this._projectIgnoreSet).sort()
    }

    // ============================================================
    // Cache-Cleanup
    // ============================================================

    /**
     * Droppt Cache-Eintraege fuer Paragraphen, die nicht (mehr) im Modell
     * sind. Deckt alle Loesch-Pfade ab (`deleteParagraph`, `setParagraphsRT`,
     * Project-Wechsel mit anderem Paragraph-Set, COW-Undo, …) — der Manager
     * hat bewusst keinen pro-Pfad-Hook, sondern wird vom Sweep-Trigger in
     * `main.js#_sweepAllParagraphsViewportFirst` mit der aktuellen Liste
     * aufgerufen. Spellcheck-Review 2026-05-10 Finding C5.
     *
     * Listener werden NICHT benachrichtigt — die View hat fuer diese Para-IDs
     * keine Lines mehr, also braucht keiner ein Repaint.
     *
     * @param {Iterable<string>} knownIds
     */
    purgeUnknownParagraphs(knownIds) {
        const known = knownIds instanceof Set ? knownIds : new Set(knownIds)
        for (const map of [this._warnings, this._generations]) {
            for (const key of map.keys()) {
                const sep = key.lastIndexOf('|')
                const paraId = sep >= 0 ? key.slice(0, sep) : key
                if (!known.has(paraId)) map.delete(key)
            }
        }
        for (const [key, t] of this._timers) {
            const sep = key.lastIndexOf('|')
            const paraId = sep >= 0 ? key.slice(0, sep) : key
            if (!known.has(paraId)) {
                clearTimeout(t)
                this._timers.delete(key)
            }
        }
    }

    // ============================================================
    // intern
    // ============================================================

    /**
     * Vereinigung Personal-Dict + Pro-Projekt-Ignore — der Worker behandelt
     * beide gleich (er kennt nur "akzeptierte Woerter"). Trennung gibt es
     * nur im Main-Thread, weil Cloud-Sync die zwei Listen separat braucht.
     */
    _mergedAcceptList() {
        const merged = new Set(this._personalSet)
        for (const w of this._projectIgnoreSet) merged.add(w)
        return Array.from(merged)
    }

    _dropWarningsForWord(word) {
        for (const [key, list] of this._warnings) {
            const filtered = list.filter(w => w.word !== word)
            if (filtered.length !== list.length) {
                this._warnings.set(key, filtered)
                this._notifyByKey(key, filtered)
            }
        }
    }

    /**
     * Re-Check fuer Paragraphen mit bereits bekannten Warnings. Wird gerufen,
     * sobald die Fallback-Sprache (lazy) geladen ist — die initialen Warnings
     * wurden ohne Fallback ermittelt, koennten also Fremdwoerter falsch
     * angemeckert haben. Leere Eintraege bleiben unberuehrt (kein Worker-
     * Roundtrip ohne Effekt).
     */
    _reCheckParagraphsWithWarnings() {
        for (const [key, list] of this._warnings) {
            if (!list || list.length === 0) continue
            const sep = key.lastIndexOf('|')
            if (sep < 0) continue
            const paraId = key.slice(0, sep)
            const field  = key.slice(sep + 1)
            this.checkNow(paraId, field)
        }
    }

    _spawnWorker() {
        const worker = this._workerFactory()
        worker.onmessage = (e) => this._onWorkerMessage(e.data)
        worker.onerror = (err) => {
            // Worker-Crash: Worker weg + Worker-gebundene State-Reste auch.
            // _loadedLangs spiegelt den State im (jetzt toten) Worker; ohne
            // den Reset wuerde der naechste setLanguage zwar einen neuen Worker
            // spawnen, aber das Dictionary nicht neu laden ("loadedLangs.has"
            // ist noch true). Der frische Worker haette dann keine Hunspell-
            // Instanz fuer die Sprache → alle Checks lieferten lautlos []
            // (siehe Spellcheck-Review 2026-05-10, Finding C1).
            // Personal-Dict + Project-Ignore bleiben bewusst stehen — das sind
            // Main-Thread-Spiegel, der naechste setLanguage resynct sie ueber
            // _mergedAcceptList in den frischen Worker.
            console.error('[SpellCheck] Worker error:', err)
            this._worker = null
            this._loadedLangs.clear()
            this._generations.clear()
            this._warnings.clear()
            for (const t of this._timers.values()) clearTimeout(t)
            this._timers.clear()
        }
        return worker
    }

    async _loadAndSendDictionary(lang, seq) {
        const { aff, dic } = await this._loader.load(lang)
        if (!this._worker) return   // wurde inzwischen gestoppt
        // Bei Aufruf aus setLanguage: wenn waehrend des Loads ein neuer
        // setLanguage angerollt ist, gehoert dieses Dict NICHT mehr in den
        // aktiven Worker (der Caller hat die Sprache evtl. schon gewechselt).
        // Default-Aufrufe ohne seq verhalten sich wie frueher.
        if (seq !== undefined && seq !== this._setLangSeq) return
        this._worker.postMessage({ type: 'load_dictionary', lang, aff, dic })
        this._loadedLangs.add(lang)
    }

    _enqueueCheck(paraId, field, generation) {
        if (!this._worker || !this._primaryLang) return
        if (!this._loadedLangs.has(this._primaryLang)) return  // Dict noch nicht da

        const para = this._getParagraph(paraId, field)
        if (!para) return

        // Pre-Filter: Code-Block-Paragraphs werden nicht gepruefft (Spec §5.1).
        // Der Adapter filtert das fuer note/info schon raus, hier ist nur
        // der text-Pfad relevant.
        if (para.codeBlock) {
            this.clearWarnings(paraId, field)
            return
        }
        // Leere Felder brauchen keinen Worker-Roundtrip — direkt loeschen.
        // Wenn das Feld vorher Warnings hatte, kommt der Listener-Notify ueber
        // clearWarnings.
        if (!para.text) {
            this.clearWarnings(paraId, field)
            return
        }

        this._worker.postMessage({
            type: 'check_paragraph',
            paragraphId:  paraId,
            field,
            generation,
            text:         para.text || '',
            primaryLang:  this._primaryLang,
            fallbackLang: this._fallbackLang,
            formatSpans:  para.formatSpans || null
        })
    }

    _onWorkerMessage(msg) {
        if (!msg || !msg.type) return
        switch (msg.type) {
            case 'paragraph_warnings': {
                const field = msg.field || FIELD_DEFAULT
                const key = _keyOf(msg.paragraphId, field)
                const expected = this._generations.get(key)
                if (msg.generation !== expected) {
                    console.debug(`[SpellCheck] stale warnings dropped for ${key} (gen ${msg.generation} vs ${expected})`)
                    return
                }
                console.debug(`[SpellCheck] ${msg.warnings?.length || 0} warnings for ${key}`)
                this._warnings.set(key, msg.warnings || [])
                this._notify(msg.paragraphId, msg.warnings || [], field)
                break
            }
            case 'dictionary_ready':
                console.debug(`[SpellCheck] dictionary_ready: ${msg.lang}`)
                // Spec §6.4: Wenn die Fallback-Sprache jetzt erst da ist,
                // wurden die initialen Sweep-Warnings ohne Fallback ermittelt.
                // Re-Check aller Paragraphen mit Warnings — englische Woerter
                // wie "deploy" verschwinden dann automatisch. Lauft auch fuer
                // die Primary-Sprache, ist dann aber meist no-op (_warnings
                // ist beim ersten dictionary_ready noch leer).
                if (msg.lang === this._fallbackLang) {
                    this._reCheckParagraphsWithWarnings()
                }
                break
            case 'dictionary_error':
                console.error(`[SpellCheck] Dictionary error for ${msg.lang}: ${msg.message}`)
                this._loadedLangs.delete(msg.lang)
                break
            case 'error':
                console.error(`[SpellCheck] Worker error: ${msg.message}`)
                break
        }
    }

    _notify(paraId, warnings, field) {
        for (const fn of this._listeners) {
            try { fn(paraId, warnings, field) } catch { /* listener-fail ignorieren */ }
        }
    }

    _notifyByKey(key, warnings) {
        const sep = key.lastIndexOf('|')
        if (sep < 0) {
            this._notify(key, warnings, FIELD_DEFAULT)
            return
        }
        const paraId = key.slice(0, sep)
        const field  = key.slice(sep + 1)
        this._notify(paraId, warnings, field)
    }
}
